import { AIEventTracker } from '@src/monitoring/utils/AIEventTracker';
import { LogManager } from '@src/utility/Logger';

import { AIFlowStep } from './AIFlowStep';

const logger = LogManager.getLogger('aiFlow:AIFlow');

export type AIFlowLog = {
  flow: string;
  category: string;
  time: Date;
  stepLabel: string;
  message: string;
};

export type AIFlowLogContext = {
  aiFlowId?: string;
  personId?: string;
  storyId?: string;
  showcaseId?: string;
  metadata?: Record<string, unknown>;
  latency_ms?: number;
  first_content_latency_ms?: number;
};

export class AIFlow {
  id?: string;
  title: string;
  category: string;
  steps: AIFlowStep[];
  currentStepIndex: number = 0;
  context: any;
  resultMapper?: (lastStepResult: any) => any;
  result: any;
  logEnabled: boolean;
  logs: AIFlowLog[] = [];
  logContext: AIFlowLogContext = {};
  onRunLogs?: (logs: AIFlowLog[], logContext: AIFlowLogContext) => Promise<string | undefined>;

  constructor(params: {
    id?: string;
    title: string;
    category: string;
    steps: AIFlowStep[];
    context: any;
    resultMapper?: (lastStepResult: any) => any;
    logEnabled: boolean;
    logContext?: AIFlowLogContext;
    onRunLogs?: (logs: AIFlowLog[], logContext: AIFlowLogContext) => Promise<string | undefined>;
  }) {
    this.id = params.id;
    this.title = params.title;
    this.category = params.category;
    this.steps = params.steps;
    this.context = params.context;
    this.resultMapper = params.resultMapper;
    this.logEnabled = params.logEnabled;
    this.logContext = params.logContext ?? {};
    this.onRunLogs = params.onRunLogs;
  }

  clone() {
    return new AIFlow({
      id: this.id,
      title: this.title,
      category: this.category,
      steps: this.steps.map((step) => step.clone()),
      context: { ...this.context },
      resultMapper: this.resultMapper,
      logEnabled: this.logEnabled,
      logContext: { ...this.logContext },
      onRunLogs: this.onRunLogs,
    });
  }

  async run() {
    const startTime = Date.now();
    logger.info('Running AI flow', { title: this.title, category: this.category, steps: this.steps.length });
    const flowTracker = AIEventTracker.start('ai_flow', {
      flow_title: this.title,
      category: this.category,
      step_count: this.steps.length,
    });
    try {
      this.log({
        stepLabel: 'Before start',
        message:
          'Initial Flow Context set: \n' +
          JSON.stringify(this.context, null, 2),
      });
      this.currentStepIndex = 0;
      for (const step of this.steps) {
        let inputMessages;
        if (step.includeMessagesFromStepId) {
          const previousStep = this.steps.find(
            (step) => step.id === step.includeMessagesFromStepId,
          );
          inputMessages = previousStep?.inputMessages!;
          inputMessages.push({ role: 'assistant', content: step.lastResponse });
        }
        step.logger = this.logEnabled
          ? (message: string) => this.log({ stepLabel: step.label, message })
          : undefined;
        step.init(this.context, inputMessages);
        await step.run();
        // Capture first content latency from the first step that got an HTTP response
        if (this.logContext.first_content_latency_ms == null && step.firstResponseAt) {
          this.logContext.first_content_latency_ms = Math.round(step.firstResponseAt - startTime);
        }
        this.context[step.id] = {
          ...(this.context[step.id] || {}),
          lastResult: step.lastResult,
        };
      }
      this.result = this.resultMapper
        ? this.resultMapper(this.steps[this.steps.length - 1].lastResult)
        : this.steps[this.steps.length - 1].lastResult;
      flowTracker.complete();
    } catch (err) {
      logger.error('Error running AI flow', { title: this.title, error: err });
      flowTracker.fail(err);
    } finally {
      if (this.logEnabled && this.onRunLogs) {
        // Capture total latency
        this.logContext.latency_ms = Math.round(Date.now() - startTime);
        logger.debug('AI flow latency captured', {
          title: this.title,
          latency_ms: this.logContext.latency_ms,
          first_content_latency_ms: this.logContext.first_content_latency_ms,
        });

        // Enrich metadata with the models actually used during execution
        if (!this.logContext.metadata) this.logContext.metadata = {};
        this.logContext.metadata.models = this.steps
          .map(s => ({ step: s.label, ...s.usedModel }))
          .filter(m => m.model_id);
        try {
          await this.onRunLogs(this.logs, this.logContext);
        } catch (err) {
          logger.error('Failed to save AI flow logs', { title: this.title, error: err });
        }
      }
    }
  }

  log(element: { stepLabel: string; message: string }) {
    this.logs.push({
      flow: this.title,
      category: this.category,
      time: new Date(),
      ...element,
    });
  }
}
